/**
 * API client — single point of contact with the backend.
 * Wraps `ky` with:
 *   - JWT access token injection
 *   - 401 → refresh attempt → retry once
 *   - Standard envelope handling: backend returns { ok, data, error }
 *
 * All callers throw on non-OK responses with a typed ApiError, which
 * TanStack Query catches and surfaces.
 */
import ky from 'ky';
import { useAuthStore } from './auth';

const API_PREFIX = '/api/v1';

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

/**
 * Standard envelope returned by every backend endpoint:
 *   { ok: true,  data: {...} }
 *   { ok: false, error: { code, message, details? } }
 */
interface Envelope<T> {
  ok: boolean;
  data?: T;
  error?: { code: string; message: string; details?: unknown };
}

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  // De-duplicate concurrent refresh attempts
  if (refreshPromise) return refreshPromise;

  const { refreshToken, setTokens, clear } = useAuthStore.getState();
  if (!refreshToken) return null;

  refreshPromise = (async () => {
    try {
      const r = await fetch(`${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      const env: Envelope<{ access_token: string; refresh_token: string }> = await r.json();
      if (!env.ok || !env.data) {
        clear();
        return null;
      }
      setTokens(env.data.access_token, env.data.refresh_token);
      return env.data.access_token;
    } catch {
      clear();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

const client = ky.create({
  prefixUrl: API_PREFIX,
  timeout: 15_000,
  hooks: {
    beforeRequest: [
      (req) => {
        const token = useAuthStore.getState().accessToken;
        if (token) req.headers.set('Authorization', `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (req, _options, res) => {
        if (res.status !== 401) return res;
        // Don't try to refresh while refreshing
        if (req.url.includes('/auth/refresh')) return res;

        const newToken = await refreshAccessToken();
        if (!newToken) return res;

        // Retry once with the new token
        req.headers.set('Authorization', `Bearer ${newToken}`);
        return ky(req);
      },
    ],
  },
});

/**
 * Call an API endpoint. Returns the `data` field directly, throws ApiError on failure.
 */
export async function api<T = unknown>(
  path: string,
  options: { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown; searchParams?: Record<string, string | number | boolean>; timeout?: number } = {},
): Promise<T> {
  const { method = 'GET', body, searchParams, timeout } = options;
  // ky uses paths relative to prefixUrl; strip leading slash
  const cleanPath = path.replace(/^\//, '');

  let res: Response;
  try {
    res = await client(cleanPath, {
      method,
      json: body,
      searchParams,
      ...(timeout != null ? { timeout } : {}),
    });
  } catch (err: any) {
    // ky throws on HTTPError — extract the envelope if we can
    if (err.response) {
      const env = await err.response.json().catch(() => null) as Envelope<T> | null;
      if (env?.error) {
        throw new ApiError(env.error.message, env.error.code, err.response.status, env.error.details);
      }
      throw new ApiError('Request failed', 'NETWORK', err.response.status);
    }
    throw new ApiError(err.message || 'Network error', 'NETWORK', 0);
  }

  const env = (await res.json()) as Envelope<T>;
  if (!env.ok) {
    throw new ApiError(env.error?.message || 'Unknown error', env.error?.code || 'UNKNOWN', res.status, env.error?.details);
  }
  return env.data as T;
}

/**
 * Download a non-JSON response (CSV, PDF) as a file. A plain <a href> can't
 * carry the Authorization header the API requires, so this fetches as a
 * blob and triggers the save via a throwaway object URL -- api() itself
 * parses every response as JSON and would choke on these bodies.
 */
export async function downloadFile(
  path: string,
  filename: string,
  searchParams?: Record<string, string | number | boolean>,
): Promise<void> {
  const token = useAuthStore.getState().accessToken;
  const qs = searchParams ? `?${new URLSearchParams(searchParams as Record<string, string>).toString()}` : '';
  const res = await fetch(`${API_PREFIX}/${path.replace(/^\//, '')}${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const env = await res.json().catch(() => null) as Envelope<unknown> | null;
    throw new ApiError(env?.error?.message || 'Download failed', env?.error?.code || 'NETWORK', res.status);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
